// ダッシュボード（Focus ＋ Pipeline を1ページ縦スクロールに統合）。
//
// 見本 _management/designs/ui_prototype/web_dashboard_prototype.html に忠実。
//   上段 Focus    : 在庫サマリー（タップで「やっつけ部屋」）＋ 今日果たす約束トップ3
//                   ＋ 待機中バー（展開）＋ 社内タスクバー（展開）
//   下段 Pipeline : フォロー漏れレール（LeakRail）＋ 案件カード（DealCard・手応え/ホット順）
//
// データ取得は useFocusData、集約ロジックは focus.ts（純関数）に委譲。
// [果たした]=status→resolved / [明日でいい]=snoozeUntil の書込みは default 側で実装し、
// プレゼン（FocusPipelineView）は props のアクション callback を呼ぶだけに保つ（テスト容易性）。
// DEC-009 厳守: 受注%・的中率・点数は一切出さない。
import React, { useEffect, useMemo, useState } from 'react';
import { User } from 'firebase/auth';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useFocusData, FocusData } from '../useFocusData';
import {
  FocusPromise, PromiseTypeDef, PROMISE_TYPES, isLeak,
  rankCustomerPromises, pickTopPromises, inventory, internalPromises,
  leakPromises, leakMeta, buildDeals, confidenceLabel, ballLabel, formatMonthDay, DealCard,
} from '../focus';
import '../components/FocusPipeline.css';

interface Props {
  user: User;
  onOpenProject: (projectId: string, projectName?: string) => void;
  // 約束の出典議事録を開く（カードの出典ラベルのタップ由来）。
  onOpenMeeting: (
    meeting: { id: string; title: string; recordedAt: Date | null },
    projectId: string,
    projectName: string,
  ) => void;
}

// ─── 表示用フォーマッタ ───
function whyLine(p: FocusPromise): string {
  const base = p.meetingDate ?? null;
  const datePart = base ? `${formatMonthDay(base)} の会議で約束` : '会議で約束';
  return `${datePart} ・ ${p.ageDays}日経過${p.estimated ? '（推定）' : ''}`;
}

// 約束カード右肩の経過/期限ピル。
const DuePills: React.FC<{ p: FocusPromise }> = ({ p }) => (
  <span className="right">
    {p.hot && <span className="fp-pill hot">🔥</span>}
    {p.dueDate ? (
      p.overdue ? (
        <span className="fp-pill danger">期限超過</span>
      ) : (
        <span className="fp-pill amber">期限: {formatMonthDay(p.dueDate)}まで</span>
      )
    ) : (
      <>
        <span className="fp-pill info">{p.ageDays}日経過</span>
        {p.estimated && <span className="fp-pill est">推定</span>}
      </>
    )}
  </span>
);

// ─── 約束カード（トップ3） ───
const PromiseCard: React.FC<{
  p: FocusPromise;
  leaving: boolean;
  onResolve: () => void;
  onSnooze: () => void;
  onOpenMeeting: () => void;
}> = ({ p, leaving, onResolve, onSnooze, onOpenMeeting }) => (
  <div className={`fp-pcard${p.overdue ? ' over' : ''}${leaving ? ' leaving' : ''}`}>
    <div className="row1">
      <span className={`fp-pill ${p.type.pill}`}>{p.type.emoji} {p.type.cardLabel}</span>
      <DuePills p={p} />
    </div>
    {/* 約束タイトルも出典議事録へのリンク（出典会議が分かるときだけ）。
        アプリ全体で「議事録由来の項目はタップで議事録へ」を統一する。 */}
    {p.sourceMeetingId ? (
      <button type="button" className="promise promise-link" onClick={onOpenMeeting} title="この約束の議事録を開く">
        {p.title}
      </button>
    ) : (
      <div className="promise">{p.title}</div>
    )}
    {/* 出典ラベルは議事録へのリンク。出典会議が分かるときだけタップ可能にする
        （sourceMeetingId が無い旧 issue 等は従来どおり非リンクのテキスト）。 */}
    {p.sourceMeetingId ? (
      <button
        type="button"
        className="why why-link"
        onClick={onOpenMeeting}
        title="この約束の議事録を開く"
        aria-label={`${whyLine(p)} の議事録を開く`}
      >
        {whyLine(p)}<span className="why-arw" aria-hidden="true">›</span>
      </button>
    ) : (
      <div className="why">{whyLine(p)}</div>
    )}
    {p.evidence && <div className="ev">「 {p.evidence} 」</div>}
    <div className="acts">
      <button className="fp-btn fp-btn-cta" onClick={onResolve}>果たした</button>
      <button className="fp-btn fp-btn-ghost" onClick={onSnooze}>明日でいい</button>
    </div>
  </div>
);

// ─── やっつけ部屋（タイプ別フィルタパネル） ───
const KnockoutPanel: React.FC<{
  type: PromiseTypeDef;
  promises: FocusPromise[];
  onClose: () => void;
  onResolve: (p: FocusPromise) => void;
  // 約束の出典議事録を開く（行タイトルのタップ由来）。top3 PromiseCard と動作を統一する。
  onOpenMeeting: (p: FocusPromise) => void;
}> = ({ type, promises, onClose, onResolve, onOpenMeeting }) => (
  <div className="fp-knock">
    <div className="k-head">
      <span className="k-ttl">{type.emoji} {type.cardLabel}（{promises.length}件）</span>
      <button className="k-close" onClick={onClose} aria-label="閉じる">×</button>
    </div>
    <div className="k-desc">この作業だけ集めて、一気に片づけます。</div>
    {promises.length === 0 ? (
      <div className="k-empty">✅ この約束タイプはありません</div>
    ) : (
      promises.map((p) => (
        <div className="fp-prow" key={p.id}>
          <span className="p-emoji">{p.type.emoji}</span>
          {/* 出典が分かる約束（sourceMeetingId あり）はタイトルをタップで議事録へ遷移。
              出典なしは従来どおり非リンク・淡色（押せそうで押せないを作らない）。
              「果たした」ボタンとは独立した兄弟要素なので誤タップしない。 */}
          <div className="p-body">
            {p.sourceMeetingId ? (
              <button
                type="button"
                className="p-title p-title-link"
                onClick={() => onOpenMeeting(p)}
                title="この約束の議事録を開く"
                aria-label={`${p.title} の議事録を開く`}
              >
                {p.title}<span className="p-arw" aria-hidden="true">›</span>
              </button>
            ) : (
              <div className="p-title p-title-nolink">{p.title}</div>
            )}
            <div className="p-meta">{p.companyName || '相手未特定'} ・ {p.ageDays}日経過{p.estimated ? '（推定）' : ''}</div>
            {p.evidence && <div className="p-ev">「 {p.evidence} 」</div>}
          </div>
          <button className="fp-btn fp-btn-cta fp-btn-sm" onClick={() => onResolve(p)}>果たした</button>
        </div>
      ))
    )}
  </div>
);

// ─── 展開リスト行（待機中 / 社内） ───
const ListRow: React.FC<{
  p: FocusPromise;
  rank?: number;
  onResolve: () => void;
  // 約束の出典議事録を開く（行タイトルのタップ由来）。top3 PromiseCard / KnockoutPanel と動作を統一する。
  onOpenMeeting: (p: FocusPromise) => void;
}> = ({ p, rank, onResolve, onOpenMeeting }) => (
  <div className="fp-wrow">
    {rank != null && <span className="w-rank">{rank}</span>}
    <span className="w-emoji">{p.type.emoji}</span>
    <div className="w-body">
      {/* 出典が分かる約束（sourceMeetingId あり）はタイトルをタップで議事録へ遷移。
          出典なしは従来どおり非リンク・淡色（押せそうで押せないを作らない）。
          「果たした」ボタンとは独立した兄弟要素なので誤タップしない。 */}
      {p.sourceMeetingId ? (
        <button
          type="button"
          className="w-title w-title-link"
          onClick={() => onOpenMeeting(p)}
          title="この約束の議事録を開く"
          aria-label={`${p.title} の議事録を開く`}
        >
          {p.title}<span className="w-arw" aria-hidden="true">›</span>
        </button>
      ) : (
        <div className="w-title w-title-nolink">{p.title}</div>
      )}
      <div className="w-meta">{p.companyName || '相手未特定'} ・ {p.ageDays}日経過{p.estimated ? '（推定）' : ''}</div>
    </div>
    <button className="fp-btn fp-btn-ghost fp-btn-sm" onClick={onResolve}>果たした</button>
  </div>
);

// ─── 案件カード（DealCard） ───
const DealCardView: React.FC<{ d: DealCard; onOpen: () => void }> = ({ d, onOpen }) => {
  const conf = confidenceLabel(d.confidenceBand);
  const ball = ballLabel(d.ball);
  const lead = d.leadPromise;
  // 代表約束の経過表示: 期限超過 / 停滞(14日以上・自社ボール) は赤字で強調（見本準拠）。
  const leadStalled = !!lead && !lead.overdue && isLeak(lead);
  return (
    <button className={`fp-deal${d.leakCount > 0 ? ' leak' : ''}`} onClick={onOpen}>
      <div className="d-top">
        <div className="d-name">{d.displayName}</div>
        {d.hot && <span className="fp-pill hot">🔥 ホット</span>}
      </div>
      {d.customerIssue && <div className="d-issue">{d.customerIssue}</div>}
      <div className="d-chips">
        {conf && <span className={`fp-pill ${conf.pill}`}>{conf.label}</span>}
        {ball && <span className={`fp-pill ${ball.pill}`}>{ball.label}</span>}
        {d.promiseCount > 0 && <span className="fp-pill gray">約束 {d.promiseCount}件</span>}
        {d.leakCount > 0 && <span className="fp-pill danger">フォロー漏れ {d.leakCount}</span>}
      </div>
      {d.hotEvidence && <div className="d-ev">「 {d.hotEvidence} 」</div>}
      {lead && (
        <div className="d-lead">
          <span className="emoji">{lead.type.emoji}</span>
          <span className="ltxt">{lead.title}</span>
          <span className={`lmeta${lead.overdue || leadStalled ? ' over' : ''}`}>
            {lead.overdue ? '期限超過' : leadStalled ? `${lead.ageDays}日停滞` : `${lead.ageDays}日経過`}
          </span>
        </div>
      )}
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════
// プレゼン本体（純表示・テスト用に data を注入できる）
// ═══════════════════════════════════════════════════════════════
export const FocusPipelineView: React.FC<{
  data: FocusData;
  onOpenProject: (projectId: string, projectName?: string) => void;
  // 約束の出典議事録を開く（出典ラベルのタップ）。
  onOpenMeeting: (p: FocusPromise) => void;
  // 成功で true / 失敗で false を返すと、楽観的に隠したカードを失敗時に復帰させる。
  onResolve: (p: FocusPromise) => void | Promise<boolean>;
  onSnooze: (p: FocusPromise) => void | Promise<boolean>;
}> = ({ data, onOpenProject, onOpenMeeting, onResolve, onSnooze }) => {
  const { loading, error, promises, promisesByProject, projects } = data;

  const [openType, setOpenType] = useState<string | null>(null); // やっつけ部屋
  const [waitOpen, setWaitOpen] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  // 楽観的に隠したカードの id（退場アニメ用の一時フラグ）。
  const [leaving, setLeaving] = useState<Set<string>>(new Set());

  // 日付はマウント時固定にせず、長時間開いたままでも日付跨ぎに追従させる（5分ごと）。
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // スナップショット（promises）が更新されたら leaving をリセットして実データに整合させる。
  // これで [元に戻す] による復活（同一 issue id の再追加）が確実に再表示される。
  useEffect(() => { setLeaving(new Set()); }, [promises]);

  const ranked = useMemo(() => rankCustomerPromises(promises, now), [promises, now]);
  const { top, rest } = useMemo(() => pickTopPromises(ranked), [ranked]);
  const inv = useMemo(() => inventory(promises, now), [promises, now]);
  const internal = useMemo(() => internalPromises(promises, now), [promises, now]);
  const leaks = useMemo(() => leakPromises(promises, now), [promises, now]);
  const deals = useMemo(() => buildDeals(projects, promisesByProject, now), [projects, promisesByProject, now]);
  const dealsWithSignal = useMemo(
    () => deals.filter((d) => d.hasDeal || d.promiseCount > 0),
    [deals]
  );

  // 果たした / 明日でいい: 退場アニメ → アクション。失敗時（false）は隠しを取り消して復帰。
  const unhide = (id: string) => setLeaving((s) => { const n = new Set(s); n.delete(id); return n; });
  const optimistic = (p: FocusPromise, action: (p: FocusPromise) => void | Promise<boolean>) => {
    setLeaving((s) => new Set(s).add(p.id));
    Promise.resolve(action(p)).then((ok) => { if (ok === false) unhide(p.id); });
  };
  const handleResolve = (p: FocusPromise) => optimistic(p, onResolve);
  const handleSnooze = (p: FocusPromise) => optimistic(p, onSnooze);

  const knockType = openType ? PROMISE_TYPES.find((t) => t.id === openType) || null : null;
  const knockPromises = knockType
    ? ranked.filter((p) => (PROMISE_TYPES.find((t) => t.id === p.rawType) ? p.rawType : 'other') === knockType.id)
    : [];

  if (loading) {
    return <div className="fp-empty"><div className="sub">読み込み中…</div></div>;
  }
  if (error) {
    return (
      <div className="fp-soft" style={{ marginTop: 12 }}>
        <div className="h">いまは情報を取得できませんでした</div>
        <div className="sub">{error}<br />電波のある場所でもう一度お試しください。</div>
      </div>
    );
  }

  const hasAnyPromise = promises.length > 0;
  const allTopDone = top.length === 0 && rest.length === 0 && internal.length === 0;

  return (
    <div className="fp-page fp-root">
      {/* ───── 上段：Focus ───── */}
      <div className="fp-focus-head">
        <div className="fp-sec-head">
          <span className="kicker">FOCUS</span>
          <span className="ttl">きょう、果たす約束。</span>
          <span className="sub">重要度順に最大3件。残りは下の全体像へ。</span>
        </div>
      </div>

      {!hasAnyPromise ? (
        <div className="fp-empty">
          <div className="ico">🎙️</div>
          <div className="h">まだ果たす約束はありません。</div>
          <div className="sub">
            アプリで会議を録音すると、相手にした約束をここに並べます。<br />
            このダッシュボードは、外出先の PC からも閲覧できます。
          </div>
        </div>
      ) : (
        <>
          {/* 在庫サマリー（タップで やっつけ部屋） */}
          <div className="fp-inv">
            <div className="inv-h">未完の約束 <b>{inv.total}件</b></div>
            <div className="chips">
              {inv.chips.map((c) => (
                <button
                  key={c.type.id}
                  className={`fp-pill ${c.type.pill} fp-chip-btn${openType === c.type.id ? ' active' : ''}`}
                  onClick={() => setOpenType(openType === c.type.id ? null : c.type.id)}
                  title={`${c.type.cardLabel}だけ集めて片づける`}
                >
                  {c.type.emoji} {c.type.label} {c.count}
                </button>
              ))}
            </div>
            {inv.unidentified > 0 && (
              <span className="note">うち相手未特定 {inv.unidentified}件</span>
            )}
          </div>

          {/* やっつけ部屋 */}
          {knockType && (
            <KnockoutPanel
              type={knockType}
              promises={knockPromises.filter((p) => !leaving.has(p.id))}
              onClose={() => setOpenType(null)}
              onResolve={handleResolve}
              onOpenMeeting={onOpenMeeting}
            />
          )}

          {/* 約束カード（トップ3） or 全消化 */}
          {top.filter((p) => !leaving.has(p.id)).length > 0 ? (
            <div className="fp-focus-grid">
              {top.map((p) => (
                <PromiseCard
                  key={p.id}
                  p={p}
                  leaving={leaving.has(p.id)}
                  onResolve={() => handleResolve(p)}
                  onSnooze={() => handleSnooze(p)}
                  onOpenMeeting={() => onOpenMeeting(p)}
                />
              ))}
            </div>
          ) : (
            allTopDone ? (
              <div className="fp-empty">
                <div className="ico">🍵</div>
                <div className="h">きょうの約束は、果たしました。</div>
                <div className="sub">おつかれさまでした。待機中の約束は、重要度順に明日また並べます。</div>
              </div>
            ) : (
              <div className="fp-soft">
                <div className="h">今日のトップ3は片づきました。</div>
                <div className="sub">下の「待機中」から次の約束を進められます。</div>
              </div>
            )
          )}

          {/* フッタ：待機中 ＋ 社内 */}
          <div className="fp-focus-foot">
            {rest.length > 0 && (
              <button className="fp-waitbar" onClick={() => setWaitOpen((v) => !v)}>
                {waitOpen ? '▾' : '▸'} 他に {rest.length}件の約束が待機中
                <span className="arw">›</span>
              </button>
            )}
            {internal.length > 0 && (
              <button className="fp-internal" onClick={() => setInternalOpen((v) => !v)}>
                🏢 社内タスク {internal.length}件 ›
              </button>
            )}
          </div>

          {waitOpen && rest.length > 0 && (
            <div className="fp-list">
              {rest.filter((p) => !leaving.has(p.id)).map((p, i) => (
                <ListRow key={p.id} p={p} rank={i + 1} onResolve={() => handleResolve(p)} onOpenMeeting={onOpenMeeting} />
              ))}
            </div>
          )}
          {internalOpen && internal.length > 0 && (
            <div className="fp-list">
              {internal.filter((p) => !leaving.has(p.id)).map((p) => (
                <ListRow key={p.id} p={p} onResolve={() => handleResolve(p)} onOpenMeeting={onOpenMeeting} />
              ))}
            </div>
          )}
        </>
      )}

      {/* ───── スクロール導線 ───── */}
      <div className="fp-flowcue"><span className="t">▾ ここから下は案件の全体像（Pipeline）</span></div>

      {/* ───── 下段：Pipeline ───── */}
      <div className="fp-sec-head" style={{ marginBottom: 16 }}>
        <span className="kicker">PIPELINE</span>
        <span className="ttl">全体を見返す。</span>
        <span className="sub">取りこぼしを拾い、手応えで案件を俯瞰する。</span>
      </div>

      {/* フォロー漏れレール */}
      {leaks.length > 0 && (
        <div className="fp-leakrail">
          <div className="lr-h">
            <span style={{ fontSize: 15 }}>🛟</span>
            <span className="t">フォロー漏れ</span>
            <span className="c">{leaks.length}件</span>
            <span className="hint">期限超過・14日以上停滞 ・ 横スクロール →</span>
          </div>
          <div className="fp-lr-track">
            {leaks.map((p) => (
              <button
                key={p.id}
                className={`fp-lchip${p.overdue ? '' : ' stall'}`}
                onClick={() => onOpenProject(p.projectId, p.projectName)}
              >
                <div className="top"><span>{p.type.emoji}</span><span className="meta">{leakMeta(p)}</span></div>
                <div className="txt">{p.title}</div>
                <div className="who">{p.companyName || p.projectName}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 案件グリッド */}
      <div className="fp-deals-head">
        <span style={{ fontSize: 15 }}>📁</span>
        <span className="t">案件</span>
        <span className="cnt">{dealsWithSignal.length}件</span>
        <span className="sortnote">手応え・ホット順</span>
      </div>

      {dealsWithSignal.length > 0 ? (
        <div className="fp-deal-grid">
          {dealsWithSignal.map((d) => (
            <DealCardView key={d.projectId} d={d} onOpen={() => onOpenProject(d.projectId, d.projectName)} />
          ))}
        </div>
      ) : (
        <div className="fp-soft">
          <div className="h">案件の手応えは、これから集まります。</div>
          <div className="sub">商談を録音するほど、案件ごとの手応えと次の一手がここに表示されます。</div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// default: データ取得＋Firestore 書込みを配線したページ
// ═══════════════════════════════════════════════════════════════
interface Toast { msg: string; actionLabel: string; action: () => void; }

const FocusPipelinePage: React.FC<Props> = ({ user, onOpenProject, onOpenMeeting }) => {
  const data = useFocusData(user.uid);
  const [toast, setToast] = useState<Toast | null>(null);

  // 出典ラベルのタップ → 約束の出典議事録へ遷移。sourceMeetingId 必須（ボタンを出す条件と一致）。
  const openMeeting = (p: FocusPromise) => {
    if (!p.sourceMeetingId) return;
    onOpenMeeting(
      { id: p.sourceMeetingId, title: p.sourceMeetingTitle || '', recordedAt: p.meetingDate },
      p.projectId,
      p.projectName,
    );
  };

  // トーストは数秒で自動的に消す（成功・失敗どちらも）。
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const issueRef = (p: FocusPromise) => doc(db, 'projects', p.projectId, 'issues', p.id);
  const errorToast = () => setToast({ msg: '更新できませんでした', actionLabel: '閉じる', action: () => setToast(null) });

  // 成功で true / 失敗で false を返す（View 側が失敗時に楽観的な隠しを取り消す）。
  const resolve = async (p: FocusPromise): Promise<boolean> => {
    try {
      await updateDoc(issueRef(p), { status: 'resolved', updatedAt: serverTimestamp() });
      setToast({
        msg: '果たしました',
        actionLabel: '元に戻す',
        action: async () => {
          try {
            await updateDoc(issueRef(p), { status: 'open', updatedAt: serverTimestamp() });
          } catch (e) { console.error('[FocusPipeline] undo resolve failed:', e); }
          setToast(null);
        },
      });
      return true;
    } catch (e) {
      console.error('[FocusPipeline] resolve failed:', e);
      errorToast();
      return false;
    }
  };

  const snooze = async (p: FocusPromise): Promise<boolean> => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(6, 0, 0, 0);
    try {
      await updateDoc(issueRef(p), { snoozeUntil: tomorrow, updatedAt: serverTimestamp() });
      setToast({
        msg: '明日また並べます',
        actionLabel: '元に戻す',
        action: async () => {
          try {
            await updateDoc(issueRef(p), { snoozeUntil: null, updatedAt: serverTimestamp() });
          } catch (e) { console.error('[FocusPipeline] undo snooze failed:', e); }
          setToast(null);
        },
      });
      return true;
    } catch (e) {
      console.error('[FocusPipeline] snooze failed:', e);
      errorToast();
      return false;
    }
  };

  return (
    <>
      <FocusPipelineView data={data} onOpenProject={onOpenProject} onOpenMeeting={openMeeting} onResolve={resolve} onSnooze={snooze} />
      {toast && (
        <div className="fp-toast">
          <span>{toast.msg}</span>
          <button className="t-undo" onClick={toast.action}>{toast.actionLabel}</button>
        </div>
      )}
    </>
  );
};

export default FocusPipelinePage;
